import type { JobProspectMatch } from '@/hooks/use-job-prospect-matches'
import type { BriefCandidate, CandidateMatch } from '@/hooks/use-briefs'
import type { CVDocument } from '@/hooks/use-cv-upload'
import type { EmployerRecentApplicantRow } from '@/hooks/use-managed-org-recent-applicants'
import type {
  ApplicantCard as ApplicantCardModel,
  ApplicantProfileSummary,
} from '@/hooks/use-opportunity-applicants'
import { savedCandidateDisplayName, talentDisplayName, talentRoleLine } from '@/lib/talent-display'

export type PersonListCardVariant = 'compact' | 'standard' | 'evaluation' | 'pipeline'

/** Identity fields only — scenario content stays in caller slots. */
export type PersonListCardModel = {
  id: string
  name: string
  subtitle?: string | null
  avatarUrl?: string | null
  colorSeed: string
}

type ApplicantProfileFields = {
  id: string
  name: string
  latest_role?: string | null
  headline?: string | null
  avatar_url?: string | null
}

function applicantProfileToPersonModel(
  rowId: string,
  profile: ApplicantProfileFields,
): PersonListCardModel {
  return {
    id: rowId,
    name: profile.name,
    subtitle: profile.latest_role || profile.headline || null,
    avatarUrl: profile.avatar_url,
    colorSeed: profile.id || rowId,
  }
}

export function jobProspectMatchToPersonModel(match: JobProspectMatch): PersonListCardModel {
  return {
    id: match.profile_id,
    name: match.name,
    subtitle: match.headline?.trim() || null,
    avatarUrl: match.avatar_url ?? null,
    colorSeed: match.profile_id,
  }
}

export function candidateMatchToPersonModel(
  candidate: CandidateMatch,
  profileSummary?: ApplicantProfileSummary | null,
): PersonListCardModel {
  return {
    id: candidate.profile_id,
    name: candidate.candidate_name,
    avatarUrl: profileSummary?.avatar_url ?? candidate.avatar_url ?? null,
    colorSeed: candidate.profile_id,
  }
}

export function employerRecentApplicantToPersonModel(
  applicant: EmployerRecentApplicantRow,
): PersonListCardModel {
  return applicantProfileToPersonModel(applicant.id, applicant.profile)
}

export function applicantCardToPersonModel(applicant: ApplicantCardModel): PersonListCardModel {
  return applicantProfileToPersonModel(applicant.id, applicant.profile)
}

export function cvDocumentToPersonModel(document: CVDocument): PersonListCardModel {
  return {
    id: document.parsed_profile_id ?? document.id,
    name: talentDisplayName(document),
    subtitle: talentRoleLine(document.profile_preview),
    avatarUrl: null,
    colorSeed: document.parsed_profile_id ?? document.id,
  }
}

export function briefCandidateToPersonModel(candidate: BriefCandidate): PersonListCardModel {
  return {
    id: candidate.profile_id,
    name: savedCandidateDisplayName(candidate),
    subtitle: talentRoleLine(candidate.profile_preview),
    avatarUrl: null,
    colorSeed: candidate.profile_id,
  }
}

export function liveCandidateToPersonModel(candidate: {
  profile_id: string
  name: string
}): PersonListCardModel {
  return {
    id: candidate.profile_id,
    name: candidate.name,
    colorSeed: candidate.profile_id,
  }
}

export function personDescriptionFromProfile(
  profile:
    | {
        headline?: string | null
        latest_role?: string | null
      }
    | null
    | undefined,
): string | null {
  if (!profile) return null
  const latestRole = profile.latest_role?.trim()
  const headline = profile.headline?.trim()
  return latestRole || headline || null
}
